import {AfterContentInit, Component, ContentChild, ElementRef, Input, TemplateRef, ViewChild} from '@angular/core';
import {ExcUploadDirective} from '../directives/exc-upload.directive';

import {ExcCoreModule, ExcTranslation} from 'exc-core';
import {ExcFileItem} from '../models/ExcFileItem';
import {DynamicInputsInterface} from '../interfaces/DynamicInputs.interface';
import {ExcListComponent, ExcListConfig, ExcViewerComponent, OverlayService} from 'exc-controls';
import {ExcSigninService} from 'exc-signin';

/**
 * A component to manage uploads. Uploads can be created, edited or deleted. The required api requests for these
 * actions are performed and handled within this component.
 * This component utilizes the ExcListComponent.
 */
@Component({
  selector: 'attachments',
  templateUrl: 'exc-attachments.component.html'
})
export class ExcAttachmentsComponent extends ExcTranslation implements AfterContentInit {

  private excUploadDirective: ExcUploadDirective;
  // @ViewChild(ExcUploadDirective) excUploadDirective: ExcUploadDirective;
  @ViewChild(ExcListComponent) excListComponent: ExcListComponent;
  @ContentChild('listItem') listItemTemplateRef: TemplateRef<ElementRef>;

  listAttachmentsConfig: ExcListConfig;
  excRestService = ExcCoreModule.Services().ExcRest;
  searchValueAttachments: string = null;

  @Input() entityName: string;
  @Input() extensionName = 'Attachment';
  @Input() allowAdd = true;
  @Input() allowRemove = true;
  @Input() allowDownload = true;
  @Input() requestObject: Record<string, any> = null;
  @Input() additionalAudits: any[] = [];
  /**
   * The id of the entity which owns the uploads
   */
  private _id;

  @Input('ID')
  get ID() {
    if (!this._id) {
      alert('Attribute ID has not been set.');
    }
    return this._id;
  }

  set ID(value) {
    this._id = value;
  }

  /**
   * A list of objects that present the dynamic input fields of the form upload component.
   */
  @Input() dynamicInputs: DynamicInputsInterface[] = [
    {
      apiValue: 'Label',
      displayValue: 'Name'
    }
  ];

  /**
   * All uploads that were created
   */
  private _uploads: ExcFileItem[] = [];
  get uploads(): ExcFileItem[] {
    return this._uploads;
  }

  set uploads(value: ExcFileItem[]) {
    this._uploads = value;
  }

  constructor(private overlayService: OverlayService, private excSec: ExcSigninService) {
    super();
  }

  ngAfterContentInit() {
    // - init upload directive
    const _self = this;
    this.excUploadDirective = new ExcUploadDirective(this.overlayService);
    this.excUploadDirective.dynamicInputs = this.dynamicInputs;
    this.excUploadDirective.addUploadEmitter.subscribe(uploads => this.onAddUpload(uploads));
    this.excUploadDirective.editUploadEmitter.subscribe(uploads => this.onEditUpload(uploads));

    // -extent list config
    this.listAttachmentsConfig = new ExcListConfig();
    if (this.allowAdd) {
      this.listAttachmentsConfig.OnAdd = async () => {
        this.excUploadDirective.startUploadDialog(undefined, _self.additionalAudits);
      };
    }
    this.listAttachmentsConfig.OnPageChanged = (pageNo, event) => {
      this.getUploads();
    };
    this.listAttachmentsConfig.OnRefresh = event1 => {
      this.getUploads();
    };
    if (this.allowRemove) {
      this.listAttachmentsConfig.OnDelete = async selectedUploads => {
        await this.onRemoveUpload(selectedUploads);
        this.getUploads();
      };
    }
    this.listAttachmentsConfig.OnSearch = function (searchValue, event?) {
      _self.searchValueAttachments = searchValue || '';
      _self.getUploads();
    };
    this.listAttachmentsConfig.OnDetailLink = (item, event, index) => this.showLightboxGallery(item, event, index);

    this.getUploads();
  }

  /**
   * Makes an api request to save the ExcFileItem and reloads data
   * @param upload - uploads that should be saved
   */
  async onAddUpload(uploads: ExcFileItem[]) {
    this.excUploadDirective.startLoadingAnimation();
    const url = this.excRestService.computeUrl(this.entityName + this.extensionName + 'C', {FKID: this.ID});
    const formData = this.createFormData(uploads);
    this.excRestService.post(url, formData, {})
      .subscribe(() => {
        this.getUploads();
        this.excUploadDirective.close();
      });
  }


  /**
   * Makes an update request for the given ExcFileItem (indeed it is an array, but used is just the first entry)
   * and reloads data
   * @param uploads - that sould be edited
   */
  onEditUpload(uploads: ExcFileItem[]) {
    console.log('upload to edit', uploads[0]);
    // TODO make rest call!
    // this.getUploads();
    this.excUploadDirective.close();
  }


  startEditUploadDialog(upload: ExcFileItem) {
    this.excUploadDirective.startUploadDialog(upload);
  }

  /**
   * Makes a delete request and reloads data
   * @param uploads - uploads to remove
   */
  private async onRemoveUpload(uploadsToRemove: ExcFileItem[]) {
    const url = this.excRestService.computeUrl(this.entityName + this.extensionName + 'D', {FKID: this.ID});
    const promises = uploadsToRemove.map(item => this.excRestService.delete(url, {ID: item.id, UPT: item.upt}, {}).toPromise());
    await Promise.all(promises);
  }

  /**
   * Gets all uploads that were saved for entity _id (foreign key) and parses the response
   * to ExcFileItem objects
   */
  private getUploads() {
    this.listAttachmentsConfig.isLoading = true;
    const url = this.excRestService.computeUrl(
      this.entityName + this.extensionName + 'R_List',
      this.requestObject ? {...this.requestObject, fts: this.searchValueAttachments || ''} : {FKID: this.ID, fts: this.searchValueAttachments || ''}
      );
    this.excRestService.sget(url, 'LEALIST', {MetaInfo: this.listAttachmentsConfig.GetMetaInfo()})
      .subscribe(data => {
        this.listAttachmentsConfig.isLoading = false;
        this.uploads = [];
        if (data.response) {
          data.response.data
            .forEach(item => this.uploads.push(ExcFileItem.fromApiResponse(item, this.dynamicInputs)));
          this.listAttachmentsConfig.paging = data.response.paging;
        } else {
          this.listAttachmentsConfig.paging = null;
        }
      });
  }


  private createFormData(uploads: ExcFileItem[]): FormData {
    // - create form data
    // -- consider misc data
    const formData = new FormData();
    Object.keys(uploads[0].misc).forEach(key => {
      formData.append(key, uploads[0].misc[key]);
    });
    if (uploads[0].AdditionalAudit !== null) {
      formData.append('AdditionalAudit', uploads[0].AdditionalAudit.toString());
    }
    // -- consider file data
    uploads.forEach(upload => formData.append('file[]', upload.asBlob, upload.fullName));
    return formData;
  }


  private showLightboxGallery(item: any, event: any, index: number) {
    ExcCoreModule.showModal(
      ExcViewerComponent,
      this.getGlobal('AttachmentsGalleryHeader'),
      'full',
      {
        galleryData: this.uploads.map(upload => ExcFileItem.toApiResponse(upload, this.dynamicInputs)),
        currentIndex: index,
        entityName: this.entityName
      }).subscribe();
  }
  getTypeTooltip(data) {
    if (data.type.startsWith('image/')) {
      return this.getRessource('TypeImage');

    } else if (data.type.startsWith('video/')) {
      return this.getRessource('TypeVideo');

    } else if (data.type === 'application/pdf') {
      return this.getRessource('TypePDF');

    } else {
      return this.getRessource('TypeOther');

    }
  }
  getAvatarClass(data) {
    if (data.type.startsWith('image/')) {
      return 'fa-image';

    } else if (data.type.startsWith('video/')) {
      return 'fa-video';

    } else if (data.type === 'application/pdf') {
      return 'fa-file-pdf';

    } else {
      return 'fa-file';

    }
    // if (data.misc.type.start())
    // switch(data.misc.MIME) {
    //   case
    // }
  }
  public startDownload(item) {
    if (!this.allowDownload) {
      return;
    }
    const url = this.excRestService.computeUrl(this.entityName + this.extensionName + 'R', {FKID: this.ID, ID: item.id, export: true});
    this.excRestService.get(url, {export: true}).subscribe(e => {
    });
  }
}
